import type { ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { SizeOption } from '@/shared/ui/size-option';
import { Swatch } from '@/shared/ui/swatch';

import { colorName, isColorAvailable, optionAvailability } from '../lib/variants';
import type { Product } from '../model/product';
import styles from './variant-picker.module.scss';

export type VariantPickerProps = {
  product: Product;
  colorId: string | null;
  size: string | null;
  onColorChange: (colorId: string) => void;
  onSizeChange: (size: string) => void;
  tip?: ReactNode;
  /**
   * "product" — PDP aside (03B); "compact" — cart edit and recommendation picker (04B);
   * "look" — rows of the "Купить весь образ" panel (03B-9).
   */
  density?: 'product' | 'compact' | 'look';
  recommendedSize?: string | null;
  showSizeLabel?: boolean;
  sizeNotes?: boolean;
  /** Shown under the swatches ("Нет в наличии: Шалфей" in the look panel). */
  colorNote?: ReactNode;
  className?: string | undefined;
  idPrefix: string;
};

/** Controlled colour and size choice with availability per colour. */
export function VariantPicker({
  product,
  colorId,
  size,
  onColorChange,
  onSizeChange,
  tip,
  density = 'product',
  recommendedSize = null,
  showSizeLabel = true,
  sizeNotes = false,
  colorNote,
  className,
  idPrefix,
}: VariantPickerProps) {
  const selectedColor = colorName(product, colorId);
  const compact = density === 'compact';
  // The look panel also shows a one-size grid ("Размер · Один размер").
  const hasSizes = density === 'look' ? product.sizes.length > 0 : product.sizes.length > 1;
  const oneSize = product.sizes.length === 1 ? (product.sizes[0] ?? null) : null;
  const sizeValue = size ?? (density === 'look' ? oneSize : null);
  // PDP and look show the tip only when there is one; the cart keeps an 18px line for it.
  const visibleTip = compact ? tip !== undefined : Boolean(tip);
  const tipNode =
    visibleTip &&
    (compact ? (
      <output className={styles.tip}>{tip}</output>
    ) : (
      <p className={styles.tip} aria-hidden="true">
        {tip}
      </p>
    ));
  return (
    <div className={cx(styles.root, className)} data-density={density}>
      {product.colors.length > 0 && (
        <fieldset className={styles.group} data-group="color" aria-labelledby={`${idPrefix}-color`}>
          <p className={styles.label} id={`${idPrefix}-color`}>
            {compact ? (
              <>
                Цвет: <b className={styles.value}>{selectedColor ?? '—'}</b>
              </>
            ) : (
              <>Цвет{selectedColor ? ` · ${selectedColor}` : ''}</>
            )}
          </p>
          <div className={styles.swatches}>
            {product.colors.map((color) => (
              <Swatch
                key={color.id}
                color={color.hex}
                name={color.name}
                size={density === 'product' ? 'md' : density === 'look' ? 'sm' : 'lg'}
                selected={color.id === colorId}
                unavailable={!isColorAvailable(product, color.id)}
                onClick={() => onColorChange(color.id)}
              />
            ))}
          </div>
          {colorNote}
        </fieldset>
      )}
      {hasSizes && (
        <fieldset className={styles.group} data-group="size" aria-labelledby={`${idPrefix}-size`}>
          {showSizeLabel && (
            <p className={styles.label} id={`${idPrefix}-size`}>
              Размер{sizeValue && !compact ? ` · ${sizeValue}` : ''}
            </p>
          )}
          <div className={styles.sizes}>
            {product.sizes.map((option) => {
              const availability = optionAvailability(product, colorId, option);
              const unavailable = availability === 'out_of_stock';
              return (
                <SizeOption
                  key={option}
                  label={option}
                  size={density === 'product' ? 'md' : 'sm'}
                  className={density === 'look' ? styles['look-size'] : undefined}
                  selected={option === sizeValue}
                  unavailable={unavailable}
                  highlighted={option === recommendedSize && option !== size}
                  note={
                    sizeNotes && unavailable
                      ? 'нет в наличии'
                      : option === recommendedSize
                        ? 'рекомендуем'
                        : undefined
                  }
                  onClick={() => onSizeChange(option)}
                />
              );
            })}
          </div>
          {!compact && tipNode}
        </fieldset>
      )}
      {(compact || !hasSizes) && tipNode}
      {!compact && <output className={styles.live}>{tip}</output>}
    </div>
  );
}
